import { User, UserPermission } from '../types/user';
import { AppModule, MODULES } from '../constants/modules';

type JwtPayload = {
  authorities?: unknown;
};

const decodeBase64Url = (value: string): string | null => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');

  try {
    if (typeof atob === 'function') {
      return atob(padded);
    }

    const buffer = (
      globalThis as typeof globalThis & {
        Buffer?: {
          from: (input: string, encoding: string) => { toString: (encoding: string) => string };
        };
      }
    ).Buffer;

    return buffer ? buffer.from(padded, 'base64').toString('binary') : null;
  } catch {
    return null;
  }
};

const parseJwtPayload = (token: string): JwtPayload | null => {
  const payload = token.split('.')[1];
  if (!payload) {
    return null;
  }

  const decoded = decodeBase64Url(payload);
  if (!decoded) {
    return null;
  }

  try {
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
};

const buildPermissionsMap = () =>
  MODULES.reduce(
    (acc, module) => {
      acc[module] = { module, canRead: false, canWrite: false };
      return acc;
    },
    {} as Record<AppModule, UserPermission>,
  );

const applyAuthority = (permissionMap: Record<AppModule, UserPermission>, authority: string) => {
  if (authority.startsWith('ROLE_')) {
    return;
  }

  if (authority.endsWith('_READ')) {
    const moduleName = authority.slice(0, -5) as AppModule;
    const permission = permissionMap[moduleName];
    if (permission) {
      permission.canRead = true;
    }
    return;
  }

  if (authority.endsWith('_WRITE')) {
    const moduleName = authority.slice(0, -6) as AppModule;
    const permission = permissionMap[moduleName];
    if (permission) {
      permission.canWrite = true;
      permission.canRead = true;
    }
  }
};

export const derivePermissionsFromAuthorities = (authorities?: unknown): UserPermission[] => {
  const permissionMap = buildPermissionsMap();
  const list = Array.isArray(authorities)
    ? authorities.filter((value): value is string => typeof value === 'string')
    : [];

  list.forEach(authority => applyAuthority(permissionMap, authority));

  return MODULES.map(module => permissionMap[module]);
};

export const derivePermissionsFromToken = (token?: string | null): UserPermission[] | null => {
  if (!token) {
    return null;
  }

  const payload = parseJwtPayload(token);
  if (!payload) {
    return null;
  }

  return derivePermissionsFromAuthorities(payload.authorities);
};

export const isAdmin = (user?: User | null): boolean => Boolean(user && user.role === 'ADMIN');

export const getModulePermission = (user: User | null | undefined, module: AppModule) => {
  if (!user || !Array.isArray(user.modules)) {
    return null;
  }
  return user.modules.find(m => m.module === module) ?? null;
};

export const canReadModule = (user: User | null | undefined, module: AppModule): boolean => {
  if (!user) {
    return false;
  }
  if (user.role === 'ADMIN') {
    return true;
  }

  return Boolean(getModulePermission(user, module)?.canRead);
};

export const canWriteModule = (user: User | null | undefined, module: AppModule): boolean => {
  if (!user) {
    return false;
  }
  if (user.role === 'ADMIN') {
    return true;
  }

  return Boolean(getModulePermission(user, module)?.canWrite);
};
